import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap } from "../api/client";

/** Words said in any recording the user may view, from the transcripts. */
export function WordSearch({
  filter,
  onOpen,
}: {
  filter: { library?: string; case?: string };
  onOpen: (utcMs: number) => void;
}) {
  const [text, setText] = useState("");
  const [words, setWords] = useState("");
  const found = useQuery({
    queryKey: ["transcripts", "search", words, filter.library, filter.case],
    enabled: words.length >= 2,
    queryFn: () =>
      unwrap(
        api.GET("/api/transcripts/search", {
          params: { query: { q: words, ...filter } },
        }),
      ),
  });
  const hits = found.data?.hits ?? [];
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Search what was said"
    >
      <Stack
        direction="row"
        component="form"
        sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}
        onSubmit={(e) => {
          e.preventDefault();
          // Again for the same words: transcripts arrive as imports finish.
          if (text.trim() === words) void found.refetch();
          else setWords(text.trim());
        }}
      >
        <TextField
          size="small"
          label="Search what was said"
          value={text}
          onChange={(e) => setText(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <Button
          type="submit"
          variant="outlined"
          disabled={text.trim().length < 2}
        >
          Search
        </Button>
        {words && (
          <Button
            onClick={() => {
              setText("");
              setWords("");
            }}
          >
            Clear
          </Button>
        )}
      </Stack>
      {found.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {found.error.message}
        </Alert>
      )}
      {words && found.data && (
        <Table size="small" aria-label="Where it was said" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>When (UTC)</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Said</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hits.map((h) => {
              const at = Date.parse(h.start_utc);
              return (
                <TableRow
                  key={`${h.file_id}${h.start_utc}`}
                  hover
                  onClick={() => onOpen(at)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Button
                      size="small"
                      sx={{ fontFamily: "monospace", p: 0, minWidth: 0 }}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        onOpen(at);
                      }}
                      aria-label={`Play from ${h.start_utc}`}
                    >
                      {new Date(at)
                        .toISOString()
                        .slice(0, 19)
                        .replace("T", " ")}
                    </Button>
                  </TableCell>
                  <TableCell>{h.source ?? "Source not identified"}</TableCell>
                  <TableCell>
                    {h.speaker != null && `Speaker ${h.speaker}: `}“{h.text}”
                    {h.translation && (
                      <Typography variant="body2" color="text.secondary">
                        In English ({h.language}): “{h.translation}”
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {hits.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  Not found in any transcript you can view.
                </TableCell>
              </TableRow>
            )}
            {found.data.truncated && (
              <TableRow>
                <TableCell colSpan={3}>
                  The latest {hits.length} are shown: add words to narrow the
                  search.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
